import React, {useState, useEffect, useMemo, useRef} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useIntl, FormattedMessage, FormattedDate, FormattedNumber } from 'react-intl';
import { get, del } from '../../services/api';
import { usePageLoader } from '../../contexts/PageLoaderContext';
import { useNotifier } from '../../contexts/NotificationContext';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal'; // <--- Импортируем модальное окно

import Title from '../../components/common/Title/Title';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import SettingsListItem from '../../components/Settings/SettingsListItem/SettingsListItem'; // <--- Наш ключевой компонент
import styles from './FormSettingsPage.module.css';
import {
    FiEye,
    FiEdit,
    FiClipboard,
    FiTrendingUp,
    FiCheck,
    FiBarChart2,
    FiLink,
    FiCalendar,
    FiDatabase, FiTrash2, FiShare2, FiDownload
} from 'react-icons/fi';
import {hapticFeedback, showBackButton} from "../../services/telegramService";
import Modal from "../../components/common/Modal/Modal"; // Больше иконок для наглядности
import QRCode from "react-qr-code"; // <--- 1. Импортируем компонент QR-кода

const FormSettingsPage = () => {
    const { formId } = useParams();
    const navigate = useNavigate();
    const intl = useIntl();
    const { showPageLoader, hidePageLoader } = usePageLoader();
    const { addNotification } = useNotifier();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);

    const [form, setForm] = useState(null);

    const qrCodeRef = useRef(null);

    useEffect(() => {
        showBackButton();
    }, []);

    useEffect(() => {
        showPageLoader();
        get(`forms/${formId}`)
            .then(response => {
                if (response.data) {
                    setForm(response.data);
                } else {
                    throw new Error('Form data not found in response.');
                }
            })
            .catch(error => {
                addNotification(
                    error.message || intl.formatMessage({ id: "formSettingsPage.loadingError" }),
                    'error'
                );
                navigate('/');
            })
            .finally(() => {
                hidePageLoader();
            });
    }, [formId, showPageLoader, hidePageLoader, addNotification, navigate, intl]);

    const deepLinkUrl = useMemo(() => {
        if (!form?.slug) return '';
        const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;
        if (!botUsername) return '';
        const payload = `view_form-${form.slug}`;
        return `https://t.me/${botUsername}?startapp=${payload}`;
    }, [form?.slug]);

    const copyToClipboard = (e) => {
        e.stopPropagation(); // Предотвращаем навигацию, если кнопка внутри ссылки/кнопки
        if (!form?.slug) return;
        const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;
        if (!botUsername) {
            console.error("Bot username is not configured in .env file!");
            addNotification("Application is not configured correctly.", 'error');
            return;
        }

        // Формируем payload: "команда-slug_формы"
        const payload = `view_form-${form.slug}`;
        const deepLinkUrl = `https://t.me/${botUsername}?startapp=${payload}`;

        navigator.clipboard.writeText(deepLinkUrl).then(() => {
            addNotification(intl.formatMessage({ id: "formSettingsPage.info.linkCopied" }), 'success');
            hapticFeedback('notification', 'success');
        });
    };

    const handleDownloadQrCode = () => {
        if (!qrCodeRef.current) return;

        // Находим SVG элемент внутри нашего ref
        const svgElement = qrCodeRef.current.querySelector('svg');
        if (!svgElement) return;

        // Превращаем SVG в строку
        const svgData = new XMLSerializer().serializeToString(svgElement);

        // Создаем временный canvas для конвертации
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Устанавливаем размер canvas равным размеру SVG
        const svgSize = svgElement.getBoundingClientRect();
        canvas.width = svgSize.width;
        canvas.height = svgSize.height;

        const img = new Image();
        // Создаем data URL из SVG строки для использования в Image
        img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));

        img.onload = () => {
            // Рисуем изображение SVG на canvas
            ctx.fillStyle = "white"; // Задаем белый фон, иначе он будет прозрачным
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);

            // Получаем data URL уже в формате PNG
            const pngUrl = canvas.toDataURL("image/png");

            // Создаем временную ссылку для скачивания
            const downloadLink = document.createElement("a");
            downloadLink.href = pngUrl;
            downloadLink.download = `quickform-qr-${form.slug || form.id}.png`; // Имя файла
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        };
    };

    const handleDelete = async () => {
        try {
            const response = await del(`forms/${form.id}`);
            addNotification(intl.formatMessage({ id: "formSettingsPage.delete.success" }), 'success');
            navigate('/'); // Перенаправляем на главную после удаления
        } catch (error) {
            addNotification(error.message, 'error');
        } finally {
            setIsDeleteModalOpen(false);
        }
    };

    // Расчет статистики
    const views = form?.views_count || 0;
    const submissions = form?.submissions_count || 0;
    const conversionRate = views > 0 ? (submissions / views) : 0;

    if (!form) {
        return null; // PageLoader отображается глобально
    }

    const handleViewForm = () => {
        navigate(`/form/${form.slug}`, {
            state: { from: 'settings' } // <--- Передаем наше состояние
        });
    };

    return (
        <div className={styles.pageContainer}>
            <Title>{form.title}</Title>
            {form.description && <p className={styles.description}>{form.description}</p>}

            {/* --- СЕКЦИЯ ОСНОВНЫХ ДЕЙСТВИЙ --- */}
            <div className={styles.settingsList}>
                <SettingsListItem
                    icon={FiEye}
                    title={intl.formatMessage({id: "formSettingsPage.viewButton"})}
                    onClick={handleViewForm}
                    // to={`/form/${form.slug}`} // Используем to для навигации
                />
                <SettingsListItem
                    icon={FiEdit}
                    title={intl.formatMessage({id: "formSettingsPage.editButton"})}
                    to={`/form/${form.id}/edit`}
                />
                <SettingsListItem
                    icon={FiDatabase}
                    title={intl.formatMessage({id: "formSettingsPage.answersButton", defaultMessage: "Все ответы"})}
                    currentValue={submissions}
                    to={`/form/${form.id}/answers`}
                />
                <SettingsListItem
                    icon={FiBarChart2}
                    title={intl.formatMessage({id: "formSettingsPage.statsButton", defaultMessage: "Статистика"})}
                    to={`/form/${form.id}/statistics`}
                />
            </div>

            {/* --- СЕКЦИЯ СТАТИСТИКИ --- */}
            <h2 className={styles.sectionTitle}><FormattedMessage id="formSettingsPage.stats.title"/></h2>
            <div className={styles.settingsList}>
                <SettingsListItem
                    icon={FiEye}
                    title={intl.formatMessage({id: "formSettingsPage.stats.views"})}
                    currentValue={`${views}`}
                />
                <SettingsListItem
                    icon={FiCheck}
                    title={intl.formatMessage({id: "formSettingsPage.stats.submissions"})}
                    currentValue={`${submissions}`}
                />
                <SettingsListItem
                    icon={FiTrendingUp}
                    title={intl.formatMessage({id: "formSettingsPage.stats.conversionRate"})}
                    currentValue={
                        <FormattedNumber value={conversionRate} style="percent" minimumFractionDigits={1}/>
                    }
                />
            </div>

            {/* --- СЕКЦИЯ ИНФОРМАЦИИ --- */}
            <h2 className={styles.sectionTitle}><FormattedMessage id="formSettingsPage.info.title"/></h2>
            <div className={styles.settingsList}>
                <SettingsListItem
                    icon={FiLink}
                    title={intl.formatMessage({id: "formSettingsPage.info.formLink"})}
                >
                    <Button variant="secondary" onClick={copyToClipboard} size="min" name="copy">
                        <FiClipboard/> <FormattedMessage id="formSettingsPage.info.copyLink"/>
                    </Button>
                </SettingsListItem>
                <SettingsListItem
                    icon={FiShare2} // <--- Новая иконка
                    title={intl.formatMessage({ id: "formSettingsPage.qrCodeButton" })}
                    onClick={() => setIsQrModalOpen(true)} // <--- Открывает модальное окно
                />
                <SettingsListItem
                    icon={FiCalendar}
                    title={intl.formatMessage({id: "formSettingsPage.info.createdOn"})}
                    currentValue={
                        <FormattedDate value={form.created_at} year="numeric" month="long" day="numeric"/>
                    }
                />
            </div>

            <h2 className={styles.sectionTitle}>
                <FormattedMessage id="formSettingsPage.delete.title"/>
            </h2>
            <div className={styles.settingsList}>
                <SettingsListItem
                    icon={FiTrash2}
                    title={intl.formatMessage({id: "formSettingsPage.delete.button"})}
                    onClick={() => setIsDeleteModalOpen(true)}
                />
            </div>

            <ConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                title={intl.formatMessage({id: "formSettingsPage.delete.confirm.title"})}
                message={intl.formatMessage({id: "formSettingsPage.delete.confirm.message"}, {formTitle: form.title})}
            />

            <Modal
                isOpen={isQrModalOpen}
                onClose={() => setIsQrModalOpen(false)}
                title={intl.formatMessage({ id: "formSettingsPage.qrCodeModalTitle" })}
            >
                <div className={styles.qrModalContent}>
                    {/* --- 4. Оборачиваем QR-код в div с ref --- */}
                    <div ref={qrCodeRef} className={styles.qrCodeWrapper}>
                        <QRCode
                            value={deepLinkUrl}
                            size={256}
                            viewBox={`0 0 256 256`}
                        />
                    </div>
                    <p className={styles.qrInstructions}>
                        <FormattedMessage id="formSettingsPage.qrCodeInstructions" />
                    </p>
                    {/* --- 5. ДОБАВЛЯЕМ КНОПКУ СКАЧИВАНИЯ --- */}
                    <Button onClick={handleDownloadQrCode} variant="primary" >
                        <FiDownload />
                        <FormattedMessage id="formSettingsPage.qrCode.downloadButton" />
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default FormSettingsPage;
